import { FC, useEffect, useMemo } from "react";
import { AdminFormFieldSubmit } from "../../../components/admin/formFields";
import { useTranslation } from "react-i18next";
import ModalGroupCameraSelector from "../management/modalGroupCameraSelector";
import { useOrganizationGroupsQuery } from "../../../api/network/adminApiServices";
import { useLoggedInUserData } from "../../../utils/user";
import { LegacyGroupEntityListingPayload } from "../../../api/types/Group";
import { APP_CONFIG } from "../../../constants/constants";
import { useCameraAdminContext } from "../context/cameraAdmin.context";
import { TGroupSelectorItem } from "../../../components/common/groupSelector.type";
import { useLazyGetDeviceListingQuery } from "../../../api/network/camerasAdminVMApiService";
import { REPORT_FILTER_BY_OPTION_VALUES } from "../constants";
import {
  CameraListingResponse,
  DeviceListingResponse,
} from "../../../api/types/Camera";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { routeUrls } from "../../../navigation/routeUrls";
import { useDispatch, useSelector } from "react-redux";
import { setModalsData, TModalsState } from "../../../api/store/commonSlice";
import PhotoCameraIcon from "../../../assets/svg/photo-camera-icon.svg";

interface SubHeaderViewProps {
  isSingleConfiguration?: boolean;
  isMassSingleConfiguration?: boolean;
  noOfCamera?: number;
  cameraName?: string;
}

const SubHeaderView: FC<SubHeaderViewProps> = ({
  isSingleConfiguration = false,
  isMassSingleConfiguration = false,
  noOfCamera = 0,
  cameraName = null
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "camera.dashboard.management.camera_settings",
  });
  const { cameraId } = useParams();

  const {
    allGroupsSelected,
    allCamerasSelected,
    selectedDataType,
    setAllGroupsSelected,
    selectedGroups,
    setSelectedGroups,
    selectedCameras,
  } = useCameraAdminContext();
  const navigate = useNavigate();
  const currentLocation = useLocation();
  const dispatch = useDispatch();
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const [getDeviceListingAPITrigger, { }] = useLazyGetDeviceListingQuery();

  // check if group hierarchy is enabled
  const accountHasGroupHierarchyEnabled = useLoggedInUserData("groupHierarchy");
  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  // fetch groups data
  const {
    data: dataOrgGroups,
    isFetching: isFetchingOrgGroups,
    error: errorOrgGroups,
  } = useOrganizationGroupsQuery(
    {
      accountid: `${thisUserOrganizationId}`,
      page: 1,
      page_size: APP_CONFIG.LISTINGS.LARGE_PAGE_SIZE,
      search: "",
    } as LegacyGroupEntityListingPayload,
    {
      refetchOnMountOrArgChange: true,
    }
  );

  // set selected groups
  useEffect(() => {
    if (accountHasGroupHierarchyEnabled === "1") return;
    if (!!dataOrgGroups && !isFetchingOrgGroups) {
      setSelectedGroups(
        dataOrgGroups?.results?.map((group) => {
          return group.groupid;
        }) ?? ([] as string[])
      );
    }
  }, [dataOrgGroups, isFetchingOrgGroups]);

  // update all groups selected state
  useEffect(() => {
    if (accountHasGroupHierarchyEnabled === "1") return;
    if (!!dataOrgGroups && !isFetchingOrgGroups) {
      setAllGroupsSelected(
        dataOrgGroups?.results?.length === selectedGroups?.length
      );
    }
  }, [selectedGroups, dataOrgGroups, isFetchingOrgGroups]);

  const MemoizedGroupSelector = useMemo(() => {
    return (
      <>
        <ModalGroupCameraSelector
          hideGroupCameraToggle={false}
          allGroups={dataOrgGroups?.results ?? []}
          filteredGroupData={
            !!dataOrgGroups && !isFetchingOrgGroups
              ? (dataOrgGroups?.results?.map((group) => ({
                ...group,
                is_hgroup: false,
                checked: (selectedGroups as string[]).includes(group.groupid),
              })) as TGroupSelectorItem[])
              : []
          }
          setFilteredGroupData={(e: TGroupSelectorItem[]) => {
            return setSelectedGroups(
              e?.filter((group) => group.checked)?.map((group) => group.groupid)
            );
          }}
          key={`group-selector-${isFetchingOrgGroups.toString()}`} // -allSelected-${JSON.stringify(allGroupsSelected)}
          isConfigurationModal={true}
          navigateToSettings={(
            selectedType,
            camera_ids,
            group_ids,
            isAllCameraSelected,
            isAllGroupSelected
          ) => {
            dispatch(
              setModalsData({
                ...modalsState,
                showPrimaryOverlay: true,
              })
            );
            // groups selected
            getDeviceListingAPITrigger({
              keyword: "",
              model: "",
              status: "",
              offset: 0,
              limit: APP_CONFIG.LISTINGS.XL_PAGE_SIZE,
              ...(selectedType === REPORT_FILTER_BY_OPTION_VALUES.CAMERA
                ? {
                  camera_ids: !!isAllCameraSelected
                    ? ""
                    : camera_ids?.length
                      ? camera_ids.join(",")
                      : "",
                  group_ids: undefined,
                }
                : {
                  group_ids: !!isAllGroupSelected
                    ? ""
                    : group_ids?.length
                      ? group_ids.join(",")
                      : "",
                  camera_ids: undefined,
                }),
            })
              .unwrap()
              .then((res: CameraListingResponse<DeviceListingResponse[]>) => {
                // navigate
                if (res?.data) {
                  const cameraIds = res?.data
                    ?.filter(
                      (device) =>
                        device.cid !== undefined &&
                        device.cid !== null &&
                        device.cid !== ""
                    )
                    ?.map((device, index) => device.cid);
                  if (APP_CONFIG.DEBUG.ALL)
                    console.log("B ===== CAMERAS COUNT", cameraIds.length);
                  dispatch(
                    setModalsData({
                      ...modalsState,
                      showGroupCameraSelector: false,
                      showPrimaryOverlay: false,
                    })
                  );
                  const currentPath = currentLocation.pathname;
                  const hasCameraId = Boolean(cameraId);
                  const pathWithoutCameraId = hasCameraId
                    ? currentPath.substring(0, currentPath.lastIndexOf("/"))
                    : currentPath;

                  if (cameraIds.length === 1) {
                    navigate(pathWithoutCameraId + `/${cameraIds[0]}`, {
                      state: {
                        isSingleConfiguration: false,
                        isMassSingleConfiguration: true,
                        cameraIds: cameraIds,
                      },
                      replace: true,
                    });
                  }
                  else {
                    navigate(isMassSingleConfiguration ? pathWithoutCameraId : ".", {
                      state: {
                        isSingleConfiguration: false,
                        isMassSingleConfiguration: false,
                        cameraIds: cameraIds,
                      },
                      replace: true,
                    });
                  }
                }
              });
          }}
        />
      </>
    );
  }, [
    modalsState.showHierarchyGroupSelector,
    accountHasGroupHierarchyEnabled,
    dataOrgGroups,
    isFetchingOrgGroups,
    selectedGroups,
    selectedDataType,
    selectedCameras,
    allGroupsSelected,
    allCamerasSelected,
    noOfCamera,
  ]);

  return (
    <div className="col-span-12 grid grid-cols-12 p-2 border-b h-20">
      <div className="col-span-3 pl-2 flex justify-between items-center font-semibold text-base leading-none tracking-normal">
        {t("alert_preferances")}
      </div>
      <div className="col-span-9 flex justify-between items-center px-2">
        <h1 className="font-semibold text-base leading-none tracking-normal text-center">
          {t("camera_configurations")}
        </h1>
        {(!isSingleConfiguration || isMassSingleConfiguration) && (
          <AdminFormFieldSubmit
            label={
              <span className="flex justify-center gap-2 whitespace-nowrap">
                {
                  <img
                    src={PhotoCameraIcon}
                    alt={"camera-icon"}
                    className="group-hover:grayscale-0 group-hover:brightness-0 group-hover:invert group-focus:grayscale-0 group-focus:brightness-0 group-focus:invert"
                  />
                }
                {`${noOfCamera} ${t("selected")}`}
              </span>
            }
            type="submit"
            variant="primary"
            onClick={() => {
              dispatch(
                setModalsData({
                  ...modalsState,
                  showGroupCameraSelector: true,
                })
              );
            }}
            customWrapperClass="cursor-not-allowed"
          />
        )}
        {isSingleConfiguration && (
          <div className="inline-flex items-center border border-accent-blue-dark rounded-md px-2.5 py-1 text-sm font-medium text-gray-800 bg-white">
            Camera ID: {cameraName}
          </div>
        )}
      </div>
      {!!dataOrgGroups && !isFetchingOrgGroups && <>{MemoizedGroupSelector}</>}
    </div>
  );
};

export default SubHeaderView;
